import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';
import MainSidebar from 'components/ui/MainSidebar';
import DirectMessagingPanel from './components/DirectMessagingPanel';
import FollowersModal from './components/FollowersModal';
import { supabase } from 'lib/supabase';
import { useAuth } from 'contexts/AuthContext';

const EXPERIENCE_COLORS = {
  Beginner: { bg: 'rgba(5,150,105,0.12)', text: '#059669' },
  Intermediate: { bg: 'rgba(212,165,32,0.12)', text: '#D4A520' },
  Advanced: { bg: 'rgba(11,31,59,0.12)', text: '#0B1F3B' },
};

const ALL_BADGES = [
  { type: 'first_investment', name: 'First Investment', icon: 'TrendingUp', desc: 'Made your first investment on WIN' },
  { type: 'portfolio_10k', name: 'Portfolio $10K', icon: 'DollarSign', desc: 'Portfolio reached $10,000 in value' },
  { type: 'referred_5', name: '5 Referrals', icon: 'UserPlus', desc: 'Referred 5 new members to WIN' },
  { type: 'completed_3_courses', name: '3 Courses', icon: 'BookOpen', desc: 'Completed 3 WIN learning courses' },
  { type: 'anniversary_1yr', name: '1-Year Member', icon: 'Calendar', desc: 'Been a WIN member for 1 year' },
  { type: 'win_verified', name: 'WIN Verified', icon: 'BadgeCheck', desc: 'Identity and credentials verified by WIN' },
  { type: 'founding_member', name: 'Founding Member', icon: 'coin_image', desc: 'One of the original WIN founding members' },
  { type: 'top_investor', name: 'Top Investor', icon: 'Award', desc: 'Recognized as a top performer on WIN' },
];

function DonutChart({ data }) {
  const total = data?.reduce((s, d) => s + d?.value, 0);
  let cumulative = 0;
  const radius = 40;
  const cx = 60;
  const cy = 60;
  const circumference = 2 * Math.PI * radius;

  return (
    <svg width="120" height="120" viewBox="0 0 120 120">
      {data?.map((segment, i) => {
        const pct = segment?.value / total;
        const offset = circumference * (1 - cumulative);
        const dash = circumference * pct;
        cumulative += pct;
        return (
          <circle
            key={i}
            cx={cx} cy={cy} r={radius}
            fill="none"
            stroke={segment?.color}
            strokeWidth="18"
            strokeDasharray={`${dash} ${circumference - dash}`}
            strokeDashoffset={offset}
            style={{ transform: 'rotate(-90deg)', transformOrigin: '60px 60px' }}
          />
        );
      })}
      <circle cx={cx} cy={cy} r={radius - 14} fill="white" />
    </svg>
  );
}

export default function MemberProfileDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('about');
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [followersCount, setFollowersCount] = useState(0);
  const [followingCount, setFollowingCount] = useState(0);
  const [badges, setBadges] = useState([]);
  const [holdings, setHoldings] = useState([]);
  const [activity, setActivity] = useState([]);
  const [messagingOpen, setMessagingOpen] = useState(false);
  const [followersModalOpen, setFollowersModalOpen] = useState(false);
  const [followersModalMode, setFollowersModalMode] = useState('followers');
  const [mentorToggle, setMentorToggle] = useState(false);
  const [mentorLoading, setMentorLoading] = useState(false);

  const isOwnProfile = user?.id === id;

  const loadProfile = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', id)?.single();
      if (error) throw error;
      setProfile(data);
      setMentorToggle(data?.is_mentor || false);

      // Follow counts
      const { count: fc } = await supabase?.from('member_follows')?.select('*', { count: 'exact', head: true })?.eq('following_id', id);
      const { count: fgc } = await supabase?.from('member_follows')?.select('*', { count: 'exact', head: true })?.eq('follower_id', id);
      setFollowersCount(fc || 0);
      setFollowingCount(fgc || 0);

      // Is following
      if (user?.id && user?.id !== id) {
        const { data: followData } = await supabase?.from('member_follows')?.select('id')?.eq('follower_id', user?.id)?.eq('following_id', id)?.maybeSingle();
        setIsFollowing(!!followData);
      }

      // Badges
      const { data: badgeData } = await supabase?.from('member_badges')?.select('*')?.eq('user_id', id);
      setBadges(badgeData || []);

      // Holdings
      if (data?.portfolio_public || user?.id === id) {
        const { data: holdingsData } = await supabase?.from('portfolio_holdings')?.select('*')?.eq('user_id', id)?.order('allocation_pct', { ascending: false })?.limit(3);
        setHoldings(holdingsData || []);
      }

      // Activity
      const { data: activityData } = await supabase?.from('member_activity')?.select('*')?.eq('user_id', id)?.order('created_at', { ascending: false })?.limit(10);
      setActivity(activityData || []);
    } catch (err) {
      console.error('Load profile error:', err);
    } finally {
      setLoading(false);
    }
  }, [id, user?.id]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const handleFollow = async () => {
    if (!user?.id || followLoading) return;
    setFollowLoading(true);
    const newState = !isFollowing;
    setIsFollowing(newState);
    setFollowersCount(prev => newState ? prev + 1 : prev - 1);
    try {
      if (newState) {
        await supabase?.from('member_follows')?.insert({ follower_id: user?.id, following_id: id });
      } else {
        await supabase?.from('member_follows')?.delete()?.eq('follower_id', user?.id)?.eq('following_id', id);
      }
    } catch {
      setIsFollowing(!newState);
      setFollowersCount(prev => newState ? prev - 1 : prev + 1);
    } finally {
      setFollowLoading(false);
    }
  };

  const handleMentorToggle = async () => {
    if (!isOwnProfile || mentorLoading) return;
    setMentorLoading(true);
    const newVal = !mentorToggle;
    setMentorToggle(newVal);
    try {
      await supabase?.from('user_profiles')?.update({ is_mentor: newVal })?.eq('id', user?.id);
    } catch {
      setMentorToggle(!newVal);
    } finally {
      setMentorLoading(false);
    }
  };

  const expColor = EXPERIENCE_COLORS?.[profile?.experience_level] || EXPERIENCE_COLORS?.Beginner;

  const donutData = [
    { value: 40, color: '#0B1F3B', label: 'Stocks' },
    { value: 30, color: '#D4A520', label: 'Real Estate' },
    { value: 20, color: '#2D4A87', label: 'Crypto' },
    { value: 10, color: '#D4A520', label: 'Other' },
  ];

  if (loading) {
    return (
      <div className="flex h-screen overflow-hidden" style={{ background: 'var(--color-surface)' }}>
        <MainSidebar isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} />
        <main className="flex-1 flex items-center justify-center" style={{ marginLeft: sidebarCollapsed ? '64px' : '240px' }}>
          <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 border-2 rounded-full animate-spin" style={{ borderColor: 'var(--color-accent)', borderTopColor: 'transparent' }} />
            <p className="font-caption text-sm" style={{ color: 'var(--color-muted-foreground)' }}>Loading profile...</p>
          </div>
        </main>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex h-screen overflow-hidden" style={{ background: 'var(--color-surface)' }}>
        <MainSidebar isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} />
        <main className="flex-1 flex items-center justify-center" style={{ marginLeft: sidebarCollapsed ? '64px' : '240px' }}>
          <div className="text-center">
            <h2 className="font-heading font-semibold text-xl mb-2" style={{ color: 'var(--color-foreground)' }}>Member not found</h2>
            <button onClick={() => navigate('/members')} className="font-caption text-sm" style={{ color: 'var(--color-accent)' }}>Back to Members</button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: 'var(--color-surface)' }}>
      <MainSidebar isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} />
      <main
        className="flex-1 overflow-y-auto transition-all duration-250"
        style={{ marginLeft: sidebarCollapsed ? '64px' : '240px' }}
      >
        {/* Back nav */}
        <div className="px-4 sm:px-6 lg:px-8 pt-6">
          <button
            onClick={() => navigate('/members')}
            className="flex items-center gap-2 font-caption text-sm mb-6 hover:opacity-80 transition-opacity"
            style={{ color: 'var(--color-muted-foreground)' }}
          >
            <Icon name="ArrowLeft" size={16} color="currentColor" />
            Back to Members
          </button>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
          {/* Profile Header Card */}
          <div
            className="bg-white rounded-2xl border overflow-hidden mb-6 shadow-sm"
            style={{ borderColor: 'var(--color-border)' }}
          >
            {/* Banner */}
            <div
              className="h-28 relative"
              style={{ background: 'linear-gradient(135deg, var(--color-primary) 0%, #2D4A87 60%, rgba(133,113,77,0.3) 100%)' }}
            >
              <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(133,113,77,0.4) 0%, transparent 50%)' }} />
            </div>

            <div className="px-6 pb-6">
              {/* Avatar row */}
              <div className="flex items-end justify-between -mt-12 mb-4">
                <div className="relative">
                  <div
                    className="w-24 h-24 rounded-full overflow-hidden border-4 shadow-lg"
                    style={{ borderColor: 'var(--color-accent)', background: 'white' }}
                  >
                    <Image
                      src={profile?.avatar_url}
                      alt={`${profile?.full_name} profile photo`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  {profile?.is_mentor && (
                    <div
                      className="absolute bottom-0 right-0 w-7 h-7 rounded-full flex items-center justify-center border-2 border-white shadow"
                      style={{ background: 'var(--color-accent)' }}
                    >
                      <Icon name="GraduationCap" size={14} color="var(--color-primary)" />
                    </div>
                  )}
                </div>
                {/* Action buttons */}
                {!isOwnProfile && (
                  <div className="flex gap-2 mt-14">
                    <button
                      onClick={handleFollow}
                      disabled={followLoading}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg font-caption text-sm font-medium transition-all"
                      style={isFollowing
                        ? { background: 'rgba(0,48,87,0.08)', color: 'var(--color-primary)', border: '1px solid rgba(0,48,87,0.2)' }
                        : { background: 'var(--color-accent)', color: 'var(--color-primary)' }
                      }
                    >
                      <Icon name={isFollowing ? 'UserCheck' : 'UserPlus'} size={15} color="currentColor" />
                      {isFollowing ? 'Following' : 'Follow'}
                    </button>
                    <button
                      onClick={() => setMessagingOpen(true)}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg font-caption text-sm font-medium transition-all"
                      style={{ border: '1px solid var(--color-primary)', color: 'var(--color-primary)', background: 'transparent' }}
                    >
                      <Icon name="MessageCircle" size={15} color="currentColor" />
                      Message
                    </button>
                    {profile?.is_mentor && (
                      <button
                        className="flex items-center gap-2 px-4 py-2 rounded-lg font-caption text-sm font-medium transition-all"
                        style={{ background: 'var(--color-primary)', color: 'white' }}
                      >
                        <Icon name="GraduationCap" size={15} color="currentColor" />
                        Request Mentorship
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Name + title */}
              <div className="mb-4">
                <div className="flex items-center gap-3 flex-wrap mb-1">
                  <h1 className="font-heading font-bold text-h3" style={{ color: 'var(--color-foreground)' }}>
                    {profile?.full_name}
                  </h1>
                  {profile?.is_win_verified && (
                    <span className="flex items-center gap-1">
                      <Icon name="BadgeCheck" size={18} color="var(--color-accent)" />
                      <span className="font-caption text-xs" style={{ color: 'var(--color-accent)' }}>WIN Verified</span>
                    </span>
                  )}
                  {profile?.is_founding_member && (
                    <span
                      className="flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-caption font-medium"
                      style={{ background: 'rgba(133,113,77,0.12)', color: 'var(--color-accent)', border: '1px solid rgba(133,113,77,0.3)' }}
                    >
                      <img
                        src="/assets/images/Golden_coins_Win_Logo-1772863636273.png"
                        alt="WIN founding member coin"
                        style={{ width: 11, height: 11, borderRadius: '50%', objectFit: 'cover' }}
                      />
                      Founding Member
                    </span>
                  )}
                </div>
                <p className="font-body text-sm mb-1" style={{ color: 'var(--color-muted-foreground)' }}>{profile?.title}</p>
                <div className="flex items-center gap-4 flex-wrap">
                  {profile?.location && (
                    <div className="flex items-center gap-1">
                      <Icon name="MapPin" size={13} color="var(--color-muted-foreground)" />
                      <span className="font-caption text-xs" style={{ color: 'var(--color-muted-foreground)' }}>{profile?.location}</span>
                    </div>
                  )}
                  <span
                    className="px-2.5 py-0.5 rounded-full text-xs font-caption font-semibold"
                    style={{ background: expColor?.bg, color: expColor?.text }}
                  >
                    {profile?.experience_level}
                  </span>
                  {profile?.is_mentor && (
                    <span
                      className="flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-caption font-medium"
                      style={{ background: 'rgba(0,48,87,0.08)', color: 'var(--color-primary)' }}
                    >
                      <Icon name="GraduationCap" size={11} color="var(--color-primary)" />
                      Mentor
                    </span>
                  )}
                </div>
              </div>

              {/* Stats row */}
              <div className="flex items-center gap-6 pb-4 mb-4 border-b" style={{ borderColor: 'var(--color-border)' }}>
                <button
                  onClick={() => { setFollowersModalMode('followers'); setFollowersModalOpen(true); }}
                  className="text-center hover:opacity-80 transition-opacity"
                >
                  <p className="font-data font-bold text-xl" style={{ color: 'var(--color-primary)' }}>{followersCount}</p>
                  <p className="font-caption text-xs" style={{ color: 'var(--color-muted-foreground)' }}>Followers</p>
                </button>
                <button
                  onClick={() => { setFollowersModalMode('following'); setFollowersModalOpen(true); }}
                  className="text-center hover:opacity-80 transition-opacity"
                >
                  <p className="font-data font-bold text-xl" style={{ color: 'var(--color-primary)' }}>{followingCount}</p>
                  <p className="font-caption text-xs" style={{ color: 'var(--color-muted-foreground)' }}>Following</p>
                </button>
                <div className="text-center">
                  <p className="font-data font-bold text-xl" style={{ color: 'var(--color-primary)' }}>{badges?.length}</p>
                  <p className="font-caption text-xs" style={{ color: 'var(--color-muted-foreground)' }}>Badges</p>
                </div>
                {profile?.win_coins > 0 && (
                  <div className="flex items-center gap-1.5 ml-auto">
                    <img
                      src="/assets/images/Golden_coins_Win_Logo-1772863636273.png"
                      alt="WIN coin"
                      style={{ width: 16, height: 16, borderRadius: '50%', objectFit: 'cover' }}
                    />
                    <span className="font-data font-semibold text-sm" style={{ color: 'var(--color-accent)' }}>
                      {profile?.win_coins?.toLocaleString()} WIN Coins
                    </span>
                  </div>
                )}
              </div>

              {/* Mentor toggle (own profile only) */}
              {isOwnProfile && (
                <div className="flex items-center justify-between p-3 rounded-xl" style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)' }}>
                  <div>
                    <p className="font-caption text-sm font-medium" style={{ color: 'var(--color-foreground)' }}>
                      {mentorToggle ? 'Available as Mentor' : 'Seeking Mentee'}
                    </p>
                    <p className="font-caption text-xs" style={{ color: 'var(--color-muted-foreground)' }}>
                      {mentorToggle ? 'Other members can request mentorship from you' : 'Toggle on to offer mentorship'}
                    </p>
                  </div>
                  <button
                    onClick={handleMentorToggle}
                    disabled={mentorLoading}
                    className="relative w-12 h-6 rounded-full transition-all duration-300 flex-shrink-0"
                    style={{ background: mentorToggle ? 'var(--color-accent)' : 'var(--color-muted)' }}
                  >
                    <span
                      className="absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all duration-300"
                      style={{ left: mentorToggle ? '26px' : '2px' }}
                    />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-white rounded-2xl border overflow-hidden shadow-sm" style={{ borderColor: 'var(--color-border)' }}>
            {/* Tab bar */}
            <div className="flex border-b" style={{ borderColor: 'var(--color-border)' }}>
              {['about', 'portfolio', 'achievements', 'activity']?.map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className="flex-1 py-3.5 font-caption text-sm font-medium capitalize transition-all relative"
                  style={activeTab === tab
                    ? { color: 'var(--color-accent)' }
                    : { color: 'var(--color-muted-foreground)' }
                  }
                >
                  {tab}
                  {activeTab === tab && (
                    <span
                      className="absolute bottom-0 left-0 right-0 h-0.5 rounded-full"
                      style={{ background: 'var(--color-accent)' }}
                    />
                  )}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div className="p-6">
              {/* ABOUT TAB */}
              {activeTab === 'about' && (
                <div className="space-y-6">
                  {/* Bio */}
                  {profile?.bio && (
                    <div>
                      <h3 className="font-heading font-semibold text-base mb-2" style={{ color: 'var(--color-foreground)' }}>About</h3>
                      <p className="font-body text-sm leading-relaxed" style={{ color: 'var(--color-muted-foreground)' }}>{profile?.bio}</p>
                    </div>
                  )}

                  {/* Investment interests */}
                  {profile?.investment_interests?.length > 0 && (
                    <div>
                      <h3 className="font-heading font-semibold text-base mb-3" style={{ color: 'var(--color-foreground)' }}>Investment Interests</h3>
                      <div className="flex flex-wrap gap-2">
                        {profile?.investment_interests?.map((interest) => (
                          <span
                            key={interest}
                            className="px-3 py-1.5 rounded-full text-sm font-caption font-medium"
                            style={{ background: 'rgba(133,113,77,0.1)', color: 'var(--color-accent)', border: '1px solid rgba(133,113,77,0.25)' }}
                          >
                            {interest}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Social links */}
                  {profile?.social_links && Object.keys(profile?.social_links)?.length > 0 && (
                    <div>
                      <h3 className="font-heading font-semibold text-base mb-3" style={{ color: 'var(--color-foreground)' }}>Connect</h3>
                      <div className="flex flex-wrap gap-3">
                        {profile?.social_links?.linkedin && (
                          <a
                            href={`https://${profile?.social_links?.linkedin}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 px-3 py-2 rounded-lg font-caption text-sm transition-all hover:opacity-80"
                            style={{ background: 'rgba(0,48,87,0.08)', color: 'var(--color-primary)' }}
                          >
                            <Icon name="Linkedin" size={15} color="currentColor" />
                            LinkedIn
                          </a>
                        )}
                        {profile?.social_links?.twitter && (
                          <a
                            href={`https://twitter.com/${profile?.social_links?.twitter?.replace('@', '')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 px-3 py-2 rounded-lg font-caption text-sm transition-all hover:opacity-80"
                            style={{ background: 'rgba(0,48,87,0.08)', color: 'var(--color-primary)' }}
                          >
                            <Icon name="Twitter" size={15} color="currentColor" />
                            {profile?.social_links?.twitter}
                          </a>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Member since + verified */}
                  <div className="flex items-center gap-4 flex-wrap pt-2 border-t" style={{ borderColor: 'var(--color-border)' }}>
                    <div className="flex items-center gap-2">
                      <Icon name="Calendar" size={14} color="var(--color-muted-foreground)" />
                      <span className="font-caption text-xs" style={{ color: 'var(--color-muted-foreground)' }}>
                        Member since {profile?.member_since ? new Date(profile.member_since)?.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : 'N/A'}
                      </span>
                    </div>
                    {profile?.is_win_verified && (
                      <div className="flex items-center gap-1.5">
                        <Icon name="BadgeCheck" size={14} color="var(--color-accent)" />
                        <span className="font-caption text-xs" style={{ color: 'var(--color-accent)' }}>WIN Verified Member</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* PORTFOLIO TAB */}
              {activeTab === 'portfolio' && (
                <div>
                  {!profile?.portfolio_public && !isOwnProfile ? (
                    <div className="flex flex-col items-center justify-center py-16 gap-4">
                      <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: 'rgba(27,42,94,0.08)' }}>
                        <Icon name="Lock" size={28} color="var(--color-primary)" />
                      </div>
                      <div className="text-center">
                        <h3 className="font-heading font-semibold text-lg mb-1" style={{ color: 'var(--color-foreground)' }}>Portfolio is Private</h3>
                        <p className="font-caption text-sm" style={{ color: 'var(--color-muted-foreground)' }}>This member has chosen to keep their portfolio private</p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {/* Privacy toggle for own profile */}
                      {isOwnProfile && (
                        <div className="flex items-center justify-between p-3 rounded-xl" style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)' }}>
                          <span className="font-caption text-sm" style={{ color: 'var(--color-foreground)' }}>
                            Portfolio is {profile?.portfolio_public ? 'Public' : 'Private'}
                          </span>
                          <button
                            onClick={async () => {
                              const newVal = !profile?.portfolio_public;
                              await supabase?.from('user_profiles')?.update({ portfolio_public: newVal })?.eq('id', user?.id);
                              setProfile(prev => ({ ...prev, portfolio_public: newVal }));
                            }}
                            className="relative w-12 h-6 rounded-full transition-all duration-300"
                            style={{ background: profile?.portfolio_public ? 'var(--color-accent)' : 'var(--color-muted)' }}
                          >
                            <span className="absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all duration-300" style={{ left: profile?.portfolio_public ? '26px' : '2px' }} />
                          </button>
                        </div>
                      )}

                      {/* Portfolio value */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="p-4 rounded-xl" style={{ background: 'var(--color-primary)' }}>
                          <p className="font-caption text-xs mb-1" style={{ color: 'rgba(255,255,255,0.6)' }}>Total Portfolio Value</p>
                          <p className="font-data font-bold text-2xl" style={{ color: 'var(--color-accent)' }}>
                            ${profile?.portfolio_value?.toLocaleString() || '0'}
                          </p>
                        </div>
                        <div className="p-4 rounded-xl" style={{ background: 'rgba(133,113,77,0.08)', border: '1px solid rgba(133,113,77,0.2)' }}>
                          <p className="font-caption text-xs mb-1" style={{ color: 'var(--color-muted-foreground)' }}>Overall ROI</p>
                          <p className="font-data font-bold text-2xl" style={{ color: profile?.portfolio_roi >= 0 ? '#059669' : '#DC2626' }}>
                            {profile?.portfolio_roi >= 0 ? '+' : ''}{profile?.portfolio_roi?.toFixed(1)}%
                          </p>
                        </div>
                      </div>

                      {/* Top holdings */}
                      {holdings?.length > 0 && (
                        <div>
                          <h3 className="font-heading font-semibold text-base mb-3" style={{ color: 'var(--color-foreground)' }}>Top Holdings</h3>
                          <div className="space-y-2">
                            {holdings?.map((h) => (
                              <div
                                key={h?.id}
                                className="flex items-center justify-between p-3 rounded-xl"
                                style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)' }}
                              >
                                <div className="flex items-center gap-3">
                                  <div
                                    className="w-9 h-9 rounded-lg flex items-center justify-center"
                                    style={{ background: 'var(--color-primary)' }}
                                  >
                                    <span className="font-data font-bold text-xs text-white">{h?.ticker}</span>
                                  </div>
                                  <div>
                                    <p className="font-data font-semibold text-sm" style={{ color: 'var(--color-foreground)' }}>{h?.ticker}</p>
                                    <p className="font-caption text-xs" style={{ color: 'var(--color-muted-foreground)' }}>{h?.allocation_pct}% allocation</p>
                                  </div>
                                </div>
                                <span
                                  className="font-data font-semibold text-sm"
                                  style={{ color: h?.gain_loss_pct >= 0 ? '#059669' : '#DC2626' }}
                                >
                                  {h?.gain_loss_pct >= 0 ? '+' : ''}{h?.gain_loss_pct?.toFixed(1)}%
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Asset allocation donut */}
                      <div>
                        <h3 className="font-heading font-semibold text-base mb-3" style={{ color: 'var(--color-foreground)' }}>Asset Allocation</h3>
                        <div className="flex items-center gap-6">
                          <DonutChart data={donutData} />
                          <div className="space-y-2">
                            {donutData?.map((d) => (
                              <div key={d?.label} className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: d?.color }} />
                                <span className="font-caption text-xs" style={{ color: 'var(--color-foreground)' }}>{d?.label}</span>
                                <span className="font-data text-xs ml-auto" style={{ color: 'var(--color-muted-foreground)' }}>{d?.value}%</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ACHIEVEMENTS TAB */}
              {activeTab === 'achievements' && (
                <div>
                  {/* WIN Coins balance */}
                  {profile?.win_coins > 0 && (
                    <div
                      className="flex items-center gap-3 p-4 rounded-xl mb-6"
                      style={{ background: 'rgba(133,113,77,0.08)', border: '1px solid rgba(133,113,77,0.2)' }}
                    >
                      <img
                        src="/assets/images/Golden_coins_Win_Logo-1772863636273.png"
                        alt="WIN gold coin"
                        style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }}
                      />
                      <div>
                        <p className="font-data font-bold text-xl" style={{ color: 'var(--color-accent)' }}>
                          {profile?.win_coins?.toLocaleString()} WIN Coins
                        </p>
                        <p className="font-caption text-xs" style={{ color: 'var(--color-muted-foreground)' }}>Earned through achievements and participation</p>
                      </div>
                    </div>
                  )}

                  {/* Badges grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                    {ALL_BADGES?.map((badgeDef) => {
                      const earned = badges?.find(b => b?.badge_type === badgeDef?.type);
                      return (
                        <div
                          key={badgeDef?.type}
                          className="group relative flex flex-col items-center gap-2 p-4 rounded-xl border text-center transition-all"
                          style={earned
                            ? { background: 'rgba(133,113,77,0.06)', borderColor: 'rgba(133,113,77,0.3)' }
                            : { background: 'var(--color-surface)', borderColor: 'var(--color-border)', filter: 'grayscale(1)', opacity: 0.5 }
                          }
                        >
                          <div
                            className="w-12 h-12 rounded-full flex items-center justify-center"
                            style={earned ? { background: 'var(--color-accent)' } : { background: 'var(--color-muted)' }}
                          >
                            {earned
                              ? <Icon name={badgeDef?.icon} size={22} color="var(--color-primary)" />
                              : <Icon name="Lock" size={18} color="var(--color-muted-foreground)" />
                            }
                          </div>
                          <p className="font-caption text-xs font-semibold" style={{ color: earned ? 'var(--color-foreground)' : 'var(--color-muted-foreground)' }}>
                            {badgeDef?.name}
                          </p>
                          {earned && (
                            <p className="font-caption text-xs" style={{ color: 'var(--color-muted-foreground)' }}>
                              {new Date(earned.earned_at)?.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                            </p>
                          )}
                          {/* Tooltip */}
                          <div
                            className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 rounded-lg text-xs font-caption text-white opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 whitespace-nowrap"
                            style={{ background: 'var(--color-primary)' }}
                          >
                            {badgeDef?.desc}
                            <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent" style={{ borderTopColor: 'var(--color-primary)' }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ACTIVITY TAB */}
              {activeTab === 'activity' && (
                <div className="space-y-4">
                  {activity?.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 gap-3">
                      <Icon name="Activity" size={32} color="var(--color-muted-foreground)" />
                      <p className="font-caption text-sm" style={{ color: 'var(--color-muted-foreground)' }}>No recent activity</p>
                    </div>
                  ) : (
                    activity?.map((item) => {
                      const iconMap = { post: 'FileText', comment: 'MessageSquare', achievement: 'Award' };
                      const colorMap = { post: 'var(--color-primary)', comment: 'var(--color-accent)', achievement: '#059669' };
                      return (
                        <div
                          key={item?.id}
                          className="flex items-start gap-3 p-4 rounded-xl border"
                          style={{ background: 'var(--color-surface)', borderColor: 'var(--color-border)' }}
                        >
                          <div
                            className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                            style={{ background: `${colorMap?.[item?.activity_type]}15` }}
                          >
                            <Icon name={iconMap?.[item?.activity_type] || 'Activity'} size={16} color={colorMap?.[item?.activity_type] || 'var(--color-primary)'} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-body text-sm leading-relaxed" style={{ color: 'var(--color-foreground)' }}>{item?.content}</p>
                            <p className="font-caption text-xs mt-1" style={{ color: 'var(--color-muted-foreground)' }}>
                              {new Date(item.created_at)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </p>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <DirectMessagingPanel
        isOpen={messagingOpen}
        onClose={() => setMessagingOpen(false)}
        initialMember={profile ? { id: profile?.id, full_name: profile?.full_name, avatar_url: profile?.avatar_url } : null}
      />
      <FollowersModal
        isOpen={followersModalOpen}
        onClose={() => setFollowersModalOpen(false)}
        profileId={id}
        mode={followersModalMode}
        profileName={profile?.full_name}
      />
    </div>
  );
}
